import React, { useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Camera, X } from 'lucide-react';

/** Reusable camera-based QR scanner.
 *  Opens a fullscreen-style modal with a live camera preview.
 *  On a successful scan it calls onScan(decodedText) and auto-closes.
 *  Uses Html5Qrcode directly (not Html5QrcodeScanner) so we control camera
 *  start/stop lifecycle and avoid the "Stop & cleanup" UI clutter.
 */
export default function QRScannerDialog({ open, onOpenChange, onScan, title = 'Scan QR Code' }) {
  const scannerRef = useRef(null);
  const containerId = 'qr-scanner-canvas';

  useEffect(() => {
    if (!open) return;
    let html5 = null;
    let active = true;
    const start = async () => {
      try {
        // Wait one tick so the DOM node is mounted
        await new Promise((r) => setTimeout(r, 50));
        if (!active) return;
        html5 = new Html5Qrcode(containerId);
        scannerRef.current = html5;
        const config = { fps: 12, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 };
        await html5.start(
          { facingMode: 'environment' },
          config,
          (decoded) => {
            if (!active) return;
            active = false;
            try { html5.stop().then(() => html5.clear()); } catch (e) { /* noop */ }
            onScan?.(decoded);
            onOpenChange?.(false);
          },
          () => { /* per-frame parse errors are ignored */ }
        );
      } catch (e) {
        // Camera unavailable / permission denied — surface a soft message
        const node = document.getElementById(containerId);
        if (node) {
          node.innerHTML = `<div class="text-sm text-rose-600 dark:text-rose-300 p-4 text-center">${(e && e.message) || 'Camera unavailable. Grant permission and retry.'}</div>`;
        }
      }
    };
    start();
    return () => {
      active = false;
      const inst = scannerRef.current;
      if (inst) {
        try { inst.stop().then(() => inst.clear()).catch(() => {}); } catch (e) { /* noop */ }
        scannerRef.current = null;
      }
    };
  }, [open, onOpenChange, onScan]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="qr-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Camera className="w-4 h-4 text-emerald-500" />{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div id={containerId} className="w-full rounded-lg overflow-hidden bg-black/70 min-h-[260px] flex items-center justify-center" />
          <p className="text-xs text-muted-foreground text-center">Point the camera at an Asset or Building QR sticker.</p>
          <div className="flex justify-end">
            <Button data-testid="qr-scanner-close" variant="outline" size="sm" onClick={() => onOpenChange(false)}><X className="w-3 h-3 mr-1" />Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
